import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, ShoppingCart, MapPinLine, CreditCard, User as UserIcon } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";

const STEPS = [
  { path: "/encomendas/nova/carrinho", label: "Carrinho", icon: ShoppingCart, key: "cart" },
  { path: "/encomendas/nova/checkout", label: "Dados de entrega", icon: MapPinLine, key: "checkout" },
  { path: "/encomendas/nova/pagamento", label: "Pagamento", icon: CreditCard, key: "payment" },
];

/**
 * Header partilhado pelas 3 etapas de "Nova Encomenda" no CRM.
 *  • Indica o cliente alvo (próprio ou view-as) para o consultor saber em
 *    nome de quem está a criar a encomenda.
 *  • Breadcrumb com 3 etapas (Carrinho → Dados → Pagamento). Steps
 *    anteriores são links; o actual é destacado; os futuros desabilitados
 *    (controlo via prop `maxStepReached`).
 */
export default function NewOrderHeader({ maxStepReached = "cart" }) {
  const { user, viewingUser } = useAuth();
  const location = useLocation();

  const target = viewingUser || user;
  const targetName = target?.NomeCompleto || target?.email || "—";
  const targetId = target?.ID_Cliente || target?.id || "—";
  const targetType = target?.TipoCliente || target?.ID_TipoCliente || "";

  const stepOrder = STEPS.map((s) => s.key);
  const maxIdx = stepOrder.indexOf(maxStepReached);
  const currentPath = location.pathname;

  return (
    <div className="mb-6" data-testid="new-order-header">
      {/* Top: back to orders + target client */}
      <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
        <Link to="/encomendas" data-testid="new-order-back"
          className="flex items-center gap-1 text-sm text-[#6B7280] hover:text-[#0F1829] transition-colors">
          <ArrowLeft size={16} /> Voltar às Encomendas
        </Link>

        <div
          data-testid="new-order-target-client"
          className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs ${
            viewingUser
              ? "bg-amber-50 text-amber-800 border border-amber-200"
              : "bg-[#F3F4F6] text-[#374151] border border-[#E5E7EB]"
          }`}
        >
          <UserIcon size={14} weight="bold" />
          <span className="uppercase tracking-[0.15em] text-[10px] font-semibold">
            {viewingUser ? "Encomenda em nome de" : "Encomenda para"}
          </span>
          <span className="font-semibold notranslate">{targetName}</span>
          <span className="text-[10px] opacity-70 notranslate">#{targetId}</span>
          {targetType ? (
            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/40 border border-white/40">
              {targetType}
            </span>
          ) : null}
        </div>
      </div>

      {/* Breadcrumb steps */}
      <nav data-testid="new-order-breadcrumb" className="flex items-center gap-2 mb-4 flex-wrap">
        {STEPS.map((s, idx) => {
          const isCurrent = currentPath.startsWith(s.path);
          const isPast = idx < maxIdx;
          const reachable = idx <= maxIdx;
          const Icon = s.icon;
          const baseCls =
            "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-colors";
          let stateCls;
          if (isCurrent) {
            stateCls = "bg-[#0F1829] text-white font-semibold";
          } else if (reachable) {
            stateCls = "bg-white border border-[#E5E7EB] text-[#374151] hover:border-[#0F1829] hover:text-[#0F1829]";
          } else {
            stateCls = "bg-[#F3F4F6] text-[#9CA3AF] cursor-not-allowed";
          }
          const content = (
            <>
              <Icon size={13} weight={isCurrent ? "fill" : "bold"} />
              <span>{s.label}</span>
              {isPast ? <span className="ml-0.5 text-[10px] opacity-70">✓</span> : null}
            </>
          );
          return (
            <div key={s.key} className="flex items-center gap-2">
              {reachable && !isCurrent ? (
                <Link to={s.path} data-testid={`new-order-step-${s.key}`} className={`${baseCls} ${stateCls}`}>
                  {content}
                </Link>
              ) : (
                <span data-testid={`new-order-step-${s.key}`} className={`${baseCls} ${stateCls}`}>{content}</span>
              )}
              {idx < STEPS.length - 1 ? <span className="text-[#D1D5DB] text-xs">›</span> : null}
            </div>
          );
        })}
      </nav>
    </div>
  );
}
